import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, X, FileText, Check, AlertCircle } from "lucide-react";
import { FactureService } from "../../services/factures/factureService";
import { ClientService } from "../../services/clients/clientService";
import { toast } from "react-hot-toast";

export const ClientFactureUpload: React.FC = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [fournisseurs, setFournisseurs] = useState<any[]>([]);
  const [selectedFournisseur, setSelectedFournisseur] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [montantSaisi, setMontantSaisi] = useState("");

  useEffect(() => {
    fetchFournisseurs();
  }, []);

  const fetchFournisseurs = async () => {
    try {
      setLoading(true);
      const response = await ClientService.getClientFournisseurs();
      // Adjust based on response structure. Assuming response.data.results or response.data
      const results = response.data.results || response.data || [];
      // Filter only active suppliers
      const activeRelations = results.filter((r: any) => r.statut === 'actif');
      setFournisseurs(activeRelations);
    } catch (error) {
      console.error("Erreur chargement fournisseurs", error);
      toast.error("Impossible de charger la liste des fournisseurs");
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const validateAndSetFile = (file: File) => {
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error("Format de fichier non supporté. Utilisez PDF, JPG ou PNG.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) { // 10MB
      toast.error("Le fichier est trop volumineux (max 10Mo).");
      return;
    }
    setFile(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !selectedFournisseur) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    const formData = new FormData();
    formData.append("fichier_original", file);
    formData.append("fournisseur", selectedFournisseur);
    // Add default values required by serializer if needed, or rely on backend defaults
    // Note: numero, dates will be extracted by OCR or defaults
    // Backend validation might require dates/amounts if OCR doesn't run immediately before save
    // But usually FactureCreationSerializer allows partial if it's an upload flow?
    // Let's verify FactureCreationSerializer fields. It requires:
    // numero, client, montant_ht, montant_ttc, date_emission, date_echeance, fichier_original
    // If these are required in serializer, we must provide them OR update serializer to make them optional for upload.
    // Ideally, for upload, we provide dummy values or the serializer should allow them to be empty waiting for OCR.
    // Let's send dummy values for now to pass validation if they are required.
    
    // Generating a temporary number
    formData.append("numero", `TEMP-${Date.now()}`);
    formData.append("montant_ht", "0");
    formData.append("montant_ttc", montantSaisi || "0");
    formData.append("tva", "0");
    const today = new Date().toISOString().split('T')[0];
    formData.append("date_emission", today);
    formData.append("date_echeance", today);

    try {
      setUploading(true);
      await FactureService.uploadFacture(formData);
      toast.success("Facture importée avec succès. Le traitement OCR va démarrer.");
      navigate("/client/factures");
    } catch (error: any) {
      console.error("Erreur upload facture", error);
      const msg = error.response?.data?.detail || "Erreur lors de l'import de la facture";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Importer une facture</h1>
        <p className="mt-1 text-sm text-gray-600">
          Téléversez vos factures pour un traitement automatique par OCR.
        </p>
      </div>

      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Sélection du fournisseur */}
            <div>
              <label htmlFor="fournisseur" className="block text-sm font-medium text-gray-700">
                Fournisseur *
              </label>
              <select
                id="fournisseur"
                value={selectedFournisseur}
                onChange={(e) => setSelectedFournisseur(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                required
              >
                <option value="">Sélectionnez un fournisseur</option>
                {fournisseurs.map((rel: any) => (
                  <option key={rel.fournisseur.id} value={rel.fournisseur.id}>
                    {rel.fournisseur.raison_sociale}
                  </option>
                ))}
              </select>
              {fournisseurs.length === 0 && !loading && (
                 <p className="mt-2 text-sm text-warning-600">
                    Vous n'avez pas de fournisseurs actifs. Veuillez d'abord inviter un fournisseur.
                 </p>
              )}
            </div>

            {/* Zone de drop */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Fichier de facture *
              </label>
              <div
                className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${
                  dragActive
                    ? "border-primary-500 bg-primary-50"
                    : "border-gray-300 hover:border-gray-400"
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="space-y-1 text-center">
                  {!file ? (
                    <>
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                        >
                          <span>Téléverser un fichier</span>
                          <input
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            className="sr-only"
                            accept=".pdf,image/*"
                            onChange={handleChange}
                          />
                        </label>
                        <p className="pl-1">ou glisser-déposer</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        PDF, PNG, JPG jusqu'à 10Mo
                      </p>
                    </>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                        <FileText className="h-8 w-8 text-primary-600" />
                        <div className="text-left">
                            <p className="text-sm font-medium text-gray-900">{file.name}</p>
                            <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setFile(null)}
                            className="ml-4 p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-500"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Saisie Montant TTC (Optionnel) */}
            <div>
              <label htmlFor="montant" className="block text-sm font-medium text-gray-700">
                Montant TTC (Optionnel)
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <input
                  type="number"
                  name="montant"
                  id="montant"
                  step="0.01"
                  min="0"
                  className="focus:ring-primary-500 focus:border-primary-500 block w-full pl-3 pr-12 sm:text-sm border-gray-300 rounded-md py-2"
                  placeholder="0.00"
                  value={montantSaisi}
                  onChange={(e) => setMontantSaisi(e.target.value)}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">XOF</span>
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                L'OCR extraira le montant automatiquement. Remplissez ce champ si le montant sur la facture est peu lisible.
              </p>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    Le traitement OCR extraira automatiquement les données (montant, date, etc.) de votre facture. Vous pourrez vérifier et corriger ces informations une fois le traitement terminé.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate("/client/factures")}
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={uploading || !file || !selectedFournisseur}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? (
                    <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Traitement en cours...
                    </>
                ) : (
                    <>
                        <Check className="h-4 w-4 mr-2" />
                        Importer la facture
                    </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
